import object from 'common/helpers/primitives/object'

import WindowResolver from './WindowResolver'


type SentryError = Error | string

type SentrySeverity = 'critical' | 'debug' | 'error' | 'fatal' | 'info' | 'log' | 'warning'

type EventProcessor = (event: object) => object

interface ISentryExtra {
  extra: {
    [key: string]: any
  }
}

interface ISentryUserContext {
  id: number
  email: string
  mixpanelId: number
}

interface IGlobalSentry {
  captureException(exception: SentryError, ...rest: any[]): string | undefined
  captureMessage(message: string, hint?: string, ...rest: any[]): string | undefined
  withScope(cb: (scope: IGlobalSentry) => string | undefined): string | undefined
  setExtras(extra: ISentryExtra): void
  addBreadcrumb(breadcrumb: { [key: string]: any }): string | undefined
  configureScope(cb: (scope: IGlobalSentry) => string | undefined): string | undefined
  setUser(userContext: ISentryUserContext): string | undefined
  addEventProcessor(event: object): any
  setLevel(level: SentrySeverity): void
  Severity: {
    Critical: 'critical'
    Debug: 'debug'
    Error: 'error'
    Fatal: 'fatal'
    Info: 'info'
    Log: 'log'
    Warning: 'warning'
  }
}

declare global {
  interface Window {
    Sentry: IGlobalSentry
  }
}

/*

  From docs:

  window.Sentry.captureException(...)

  Usage:

  sentry.captureException(...)

 */

const windowEntity = 'Sentry'

const methods = {
  captureException: true,
  addEventProcessor: true,
  captureBreadcrumb: true,
  setUserContext: true,
  captureMessage: true,
}

interface ISentry {
  captureException(error: SentryError, extra?: ISentryExtra, level?: SentrySeverity): void
  addEventProcessor(fn: EventProcessor): void
  captureBreadcrumb(breadcrumb: any): void
  setUserContext(userContext: ISentryUserContext): void
  captureMessage(title: string, extra?:ISentryExtra)
}

const sentry = new WindowResolver({
  name: windowEntity,
  methods,
  logToConsole: false,
  resolver: () => Boolean(window.Sentry),
  eventHandler: (methodName: keyof ISentry, ...args: any[]): string  | undefined => {
    if (methodName === 'captureException') {
      window.Sentry.withScope((scope) => {
        const ceArgs      = <[ SentryError ] | [ SentryError, ISentryExtra, SentrySeverity ]>args || []
        const severity    = args[2] || window.Sentry.Severity.Error
        let globalMethod  = window.Sentry.captureException

        scope.setLevel(severity)

        if (typeof ceArgs[0] === 'string') {
          globalMethod = window.Sentry.captureMessage
        }

        if (object.isObject(ceArgs[1]) && ceArgs[1].extra) {
          return window.Sentry.withScope((scope) => {
            scope.setExtras(ceArgs[1])
            return globalMethod(ceArgs[0])
          })
        }

        const [ error, ...restArgs ] = ceArgs
        return globalMethod(error, ...restArgs)
      })
    }

    if (methodName === 'captureMessage') {
      const params = args || []
      const title  = params[0]
      const extra  = params[1]

      if (object.isObject(extra)) {
        return window.Sentry.withScope((scope) => {
          scope.setExtras(extra)
          return window.Sentry.captureMessage(title)
        })
      }

      return window.Sentry.captureMessage(title)
    }

    if (methodName === 'addEventProcessor') {
      const cdcArgs = <[ EventProcessor ]>args || []

      window.Sentry.configureScope((scope) => scope.addEventProcessor(cdcArgs[0]))
    }

    if (methodName === 'captureBreadcrumb') {
      const cbArgs = <[ { [key: string]: any } ]>args || []

      return window.Sentry.addBreadcrumb(cbArgs[0])
    }

    if (methodName === 'setUserContext') {
      const sucArgs = <[ ISentryUserContext ]>args || []

      return window.Sentry.configureScope((scope) => scope.setUser(sucArgs[0]))
    }
  },
}) as unknown as ISentry
// TODO: think how to make this work without that shit (as, as)


export default sentry

export {
  windowEntity,
  methods,
}
